import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, from, of, switchMap, tap, Observable, take, distinctUntilChanged, pipe, shareReplay, filter, skipWhile, Subscription, map, defer, throwError } from 'rxjs';
import { User as AppUser, Role } from '../models/user';
import { Router } from '@angular/router';
import {
  Auth,
  GoogleAuthProvider,
  User,
  user,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendEmailVerification,
  onAuthStateChanged,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  deleteUser,
  reauthenticateWithPopup,
  linkWithCredential
} from '@angular/fire/auth';
import {
  Firestore,
  collection,
  deleteDoc,
  doc,
  docData,
  getDocs,
  query,
  setDoc,
  where
} from '@angular/fire/firestore';
import { OrderStatus } from '../models/order';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject = new BehaviorSubject<AppUser | null | undefined>(undefined);
  user$ = this.userSubject.asObservable();
  private readonly afAuth = inject(Auth);
  private readonly afs = inject(Firestore);
  private readonly router = inject(Router);
  private firestore = inject(Firestore)

  // Firebase Auth user observable
  private readonly firebaseUser$ = user(this.afAuth)

  constructor() {
    onAuthStateChanged(this.afAuth, (firebaseUser: User | null) => {
      if (firebaseUser) {
        const userDocRef = doc(this.afs, `users/${firebaseUser.uid}`);
        // unsubscribe old listener if exists
        if (this.userSub) {
          this.userSub.unsubscribe();
        }
        this.userSub = docData(userDocRef).subscribe({
          next: (user) => { this.userSubject.next(user as AppUser) },
          error: () => this.userSubject.next(null)
        });
      } else {
        this.userSubject.next(null);
        if (this.userSub) {
          this.userSub.unsubscribe();
          this.userSub = undefined;
        }
      }
    });
  }

  private userSub?: Subscription;

  async isEmailPasswordUser(): Promise<boolean> {
    const user = await this.afAuth.currentUser;
    if (!user) return false;

    return !user.providerData.some(p => p.providerId === 'google.com');
  }


  get currentUser(): AppUser | null | undefined {
    return this.userSubject.value;
  }

  get isAuthenticated(): boolean {
    return this.currentUser !== null && this.currentUser !== undefined;
  }

  private updateUserData(user: Partial<AppUser>): Promise<void> {
    if (!user.id) {
      return Promise.reject(new Error('User ID is required'));
    }

    const userRef = doc(this.afs, `users/${user.id}`);
    return setDoc(userRef, user, { merge: true });
  }

  resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.afAuth, email);
  }

  emailSignUp(email: string, password: string, fullName: string): Observable<void> {
    return from(createUserWithEmailAndPassword(this.afAuth, email, password)).pipe(
      switchMap(async (credential) => {
        if (!credential.user) {
          throw new Error('User creation failed');
        }
        await sendEmailVerification(credential.user);
        const userData: Partial<AppUser> = {
          id: credential.user.uid,
          name: fullName,
          email: credential.user.email || email,
          profilePicture: credential.user.photoURL || '',
          role: Role.CUSTOMER,
          createdAt: new Date().toISOString(),
          emailVerified: credential.user.emailVerified
        };

        // return from(this.updateUserData(userData));
        return this.updateUserData(userData);
      }),
      tap(() => {
        this.router.navigate(['/verify-email']);
      })
    );
  }

  emailLogin(email: string, password: string): Observable<User> {
    return from(signInWithEmailAndPassword(this.afAuth, email, password)).pipe(
      switchMap(credential => {
        if (!credential.user) throw new Error('Login failed');
        // wait for userSubject to emit the actual AppUser
        return this.user$.pipe(
          filter(user => !!user),       // only proceed when user exists
          take(1),
          tap(() => {
            const tree = this.router.parseUrl(this.router.url);
            const returnUrl = tree.queryParams['returnUrl'];
            if (returnUrl) {
              this.router.navigateByUrl(returnUrl);
            } else {
              this.router.navigate(['/']);
            }
          }),
          map(() => credential.user)
        );
      })
    );
  }

  googleLogin(): Observable<void> {

    const provider = new GoogleAuthProvider();

    return defer(() =>
      signInWithPopup(this.afAuth, provider)
    ).pipe(
      switchMap(credential => {
        if (!credential?.user) {
          return throwError(() => new Error('Google login failed'));
        }

        const userData: Partial<AppUser> = {
          id: credential.user.uid,
          name: credential.user.displayName ?? 'Google User',
          email: credential.user.email ?? '',
          profilePicture: credential.user.photoURL ?? '',
          role: Role.CUSTOMER,
          createdAt: new Date().toISOString(),
          emailVerified: true
        };

        return from(this.updateUserData(userData));
      }),
      tap(() => {
        this.router.navigate(['/']);
      })
    );
  }

  logout(): Observable<void> {
    return from(signOut(this.afAuth)).pipe(
      tap(() => {
        this.userSubject.next(null);
        this.router.navigate(['/sign-in']); // Or your logout landing page
      })
    );
  }

  async resendVerificationEmail(): Promise<void> {
    const u = await this.afAuth.currentUser;
    if (!u) {
      throw new Error('No logged in user');
    }
    return sendEmailVerification(u);
  }

  // async checkEmailVerified(): Promise<void> {
  //   const user = await this.afAuth.currentUser;

  //   if (!user) {
  //     return;
  //   }

  //   if (user.emailVerified) {
  //     // Update the userSubject to reflect the verified status
  //     await this.updateUserData({ id: user.uid, emailVerified: true });
  //   } else {
  //     console.log("Email is not verified");
  //   }
  // }

  async checkEmailVerified(): Promise<void> {
    const user = await this.afAuth.currentUser;

    if (!user) {
      return;
    }

    // IMPORTANT: Reload user to get latest emailVerified status
    await user.reload();

    const refreshedUser = await this.afAuth.currentUser;

    if (refreshedUser?.emailVerified) {
      await this.updateUserData({
        id: refreshedUser.uid,
        emailVerified: true
      });
    } else {
    }
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const user = this.afAuth.currentUser
    if (!user) {
      throw new Error('User not logged in.')
    }

    if (!user.email) {
      throw new Error('user email not available.')
    }

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    await updatePassword(user, newPassword);
  }

  async deleteAccount(password: string) {
    const user = this.afAuth.currentUser;
    if (!user || !user.email) throw new Error('User not logged in');

    try {
      // Check active orders first
      const hasOrders = await this.hasActiveOrders(user.uid);
      if (hasOrders) {
        throw new Error('You cannot delete your account while orders are still in progress.');
      }

      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      await deleteDoc(doc(this.firestore, `users/${user.uid}`));
      await deleteUser(user);

    } catch (err) {
      console.error('Error deleting account:', err);
      throw err;
    }
  }

  async deleteGoogleAccount(): Promise<void> {
    const user = this.afAuth.currentUser;
    if (!user) throw new Error('No signed-in user found.');

    try {
      // Check active orders first
      const hasOrders = await this.hasActiveOrders(user.uid);
      if (hasOrders) {
        throw new Error('You cannot delete your account while orders are still in progress.');
      }

      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(user, provider);

      await deleteDoc(doc(this.firestore, `users/${user.uid}`));
      await deleteUser(user);

    } catch (error) {
      console.error('Failed to delete Google user:', error);
      throw error;
    }
  }


  async hasActiveOrders(userId: string): Promise<boolean> {
    const ordersRef = collection(this.firestore, 'orders');

    const q = query(
      ordersRef,
      where('userId', '==', userId),
      where('status', 'in', [
        OrderStatus.PENDING,
        OrderStatus.CONFIRMED,
        OrderStatus.PACKING,
        OrderStatus.SHIPPED
      ])
    );

    const snapshot = await getDocs(q);
    return !snapshot.empty; // true = has active orders
  }

  //If you want users to later log in without Google (using an email/password)
  async linkEmailPassword(auth: Auth, email: string, newPassword: string) {
    const user = auth.currentUser;
    if (!user) throw new Error('No logged-in user');

    const credential = EmailAuthProvider.credential(email, newPassword);

    try {
      // Link the new email/password to the existing Google user
      const result = await linkWithCredential(user, credential);
      // Optionally, you can later change the password
      await updatePassword(result.user, newPassword);
    } catch (err) {
      console.error('Error linking email/password:', err);
    }
  }
}